import { datadogLogs } from "@datadog/browser-logs";
import axios from "axios";
import { useMemo, useState } from "react";

import { useScript } from "./useScript";

export interface UserInfo {
  id: string;
  email: string;
  verified_email: string;
  name: string;
  given_name: string;
  family_name: string;
  picture: string;
  locale: string;
}

export type GoogleSignInResponse = {
  token: google.accounts.oauth2.TokenResponse;
  userInfo: UserInfo;
};

export interface GoogleConnectResponse {
  access_token: string;
  expiry_date: number;
  id_token: string;
  refresh_token: string;
  scope: string;
  token_type: string;
}

const scopes: string[] = [
  "https://www.googleapis.com/auth/classroom.courses.readonly",
  "https://www.googleapis.com/auth/classroom.coursework.me",
  "https://www.googleapis.com/auth/classroom.coursework.students",
  "https://www.googleapis.com/auth/classroom.profile.emails",
  "https://www.googleapis.com/auth/classroom.profile.photos",
  "https://www.googleapis.com/auth/classroom.rosters.readonly",
];

const scopesStudent: string[] = [
  "https://www.googleapis.com/auth/classroom.coursework.me",
];

export const useGoogleSignIn = (config: { tokenServerUrl: string }) => {
  useScript("https://accounts.google.com/gsi/client");
  const [user, setUser] = useState<any>(undefined);
  const [userInfo, setUserInfo] = useState<UserInfo>();

  return useMemo(() => {
    /** Get code token to persist oauth authorization to user's Google account. */
    const connect = async (
      params?: { scopes: string[] },
      callback?: (response: GoogleConnectResponse) => void,
    ) => {
      if (!process.env.REACT_APP_GOOGLE_CLIENT_ID) {
        console.error("Missing GOOGLE_CLIENT_ID");
        return;
      }

      const client = google.accounts.oauth2.initCodeClient({
        client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
        scope: ["email", "profile", ...(params?.scopes || [])].join(" "),
        callback: async (rsp: any) => {
          try {
            const url = `${config.tokenServerUrl}/auth/google/oauth2/token`;
            const result = await axios.get<GoogleConnectResponse>(url, {
              params: rsp,
            });
            callback?.(result.data);
          } catch (error) {
            alert(
              "There was an error connecting to ExamSpark to generate user access token.",
            );
          }
        },
      });

      client.requestCode();
    };

    const refreshAccessToken = async (refreshToken: string) => {
      try {
        const result = await axios.get(
          `${config.tokenServerUrl}/auth/google/oauth2/refresh`,
          {
            params: {
              refreshToken,
            },
          },
        );
        datadogLogs.logger.info("Google Sign In: User Access Token Refreshed", {
          token: result,
        });
        return result.data;
      } catch (error: any) {
        datadogLogs.logger.info(
          `Google Sign In: User Access Token Refresh Failed`,
          {
            error,
          },
        );
        if (error.response?.data?.error) {
          // throw if google sends back meaningful data about the error
          throw new Error(error.response.data.error);
        } else {
          // throw generic error on a 404 or similar
          throw new Error(error.message);
        }
      }
    };

    const verifyScopes = (expected: string[], actual: string[]) => {
      let verified = true;

      for (const scope of expected) {
        if (!actual.includes(scope)) {
          verified = false;
        }
      }

      return verified;
    };

    const fetchUserInfo = async (accessToken?: string) => {
      return await axios.get(
        `https://www.googleapis.com/oauth2/v1/userinfo?access_token=${
          accessToken || user.access_token
        }`,
      );
    };

    /** Get access_token for quick browser-based queries. */
    const login = (
      params?: { scopes: string[] },
      callback?: (rsp: {
        token: google.accounts.oauth2.TokenResponse;
        userInfo: UserInfo;
      }) => void,
    ) => {
      if (!process.env.REACT_APP_GOOGLE_CLIENT_ID) {
        console.error("Missing GOOGLE_CLIENT_ID");
        return;
      }

      try {
        const client = google.accounts.oauth2.initTokenClient({
          client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
          scope: ["email", "profile", ...(params?.scopes || [])].join(" "),
          callback: async (response) => {
            console.log("login!", response);
            datadogLogs.logger.info("Google Sign In: Logged In", {
              response,
            });
            if (response.error) return;

            const userInfo = (await fetchUserInfo(response.access_token)).data;
            datadogLogs.logger.info("Google Sign In: User", {
              userInfo,
            });
            callback?.({
              token:
                response as unknown as google.accounts.oauth2.TokenResponse,
              userInfo,
            });
            setUser(response);
            setUserInfo(userInfo);
          },
        });
        client.requestAccessToken();
      } catch (error: any) {
        throw new Error(error);
      }
    };

    return {
      user,
      userInfo,
      fetchUserInfo,
      refreshAccessToken,
      login,
      connect,
      verifyScopes,
      scopes: {
        classroom: scopes,
        classroomStudent: scopesStudent,
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.tokenServerUrl, user, userInfo]);
};
